import { useCallback, useEffect, useState } from "react";
import type { SetURLSearchParams } from "react-router-dom";
import type { WatchRule } from "@/shared/lib/types";
import type { WatchDetail } from "@/domains/watches/components/WatchDetailPanel";

type ApiFn = (
  method: string,
  path: string,
  body?: unknown,
  options?: { silent?: boolean },
) => Promise<unknown>;

type WatchDetailResponse = WatchDetail;

export function useWatchHistory(params: {
  api: ApiFn;
  watches: WatchRule[];
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  onLoadError?: () => void;
}) {
  const {
    api,
    watches,
    searchParams,
    setSearchParams,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    onLoadError,
  } = params;
  const [expandedWatches, setExpandedWatches] = useState<Record<string, boolean>>({});
  const [detailData, setDetailData] = useState<Record<string, WatchDetail>>({});
  const [detailLoading, setDetailLoading] = useState<Record<string, boolean>>({});
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});
  const [refreshingLogsWatchId, setRefreshingLogsWatchId] = useState<string | null>(null);

  const fetchDetail = useCallback(async (watchId: string) => {
    setDetailLoading((prev) => ({ ...prev, [watchId]: true }));
    setDetailErrors((prev) => {
      const remaining = { ...prev };
      delete remaining[watchId];
      return remaining;
    });
    try {
      const data = await api("GET", `/api/watches/${watchId}`) as WatchDetailResponse;
      if (!data) throw new Error("Watch details are unavailable");
      setDetailData((prev) => ({ ...prev, [watchId]: data }));
      return data;
    } catch (error) {
      setDetailErrors((prev) => ({
        ...prev,
        [watchId]: error instanceof Error ? error.message : "Unable to load watch details",
      }));
      throw error;
    } finally {
      setDetailLoading((prev) => {
        const remaining = { ...prev };
        delete remaining[watchId];
        return remaining;
      });
    }
  }, [api]);

  useEffect(() => {
    const watchId = searchParams.get("watchId");
    if (!watchId || watches.length === 0) return;

    const watchIndex = watches.findIndex(
      (watch) => watch.watch_id === watchId || watch._id === watchId,
    );
    if (watchIndex === -1) return;

    const targetWatch = watches[watchIndex];
    const idToUse = String(targetWatch.watch_id || targetWatch._id || "");
    const pageOfWatch = Math.floor(watchIndex / itemsPerPage);
    if (currentPage !== pageOfWatch) {
      setCurrentPage(pageOfWatch);
    }

    if (!expandedWatches[idToUse]) {
      // Deep-link hydration intentionally synchronizes URL state into row expansion.
      setExpandedWatches((prev) => ({ ...prev, [idToUse]: true }));
    }

    if (!detailData[idToUse]) {
      fetchDetail(idToUse).catch(() => onLoadError?.());
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("watchId");
    setSearchParams(newParams, { replace: true });
  }, [
    api,
    currentPage,
    expandedWatches,
    detailData,
    fetchDetail,
    itemsPerPage,
    onLoadError,
    searchParams,
    setCurrentPage,
    setSearchParams,
    watches,
  ]);

  const toggleExpand = async (watchId: string) => {
    const isNowOpen = !expandedWatches[watchId];
    setExpandedWatches((prev) => ({ ...prev, [watchId]: isNowOpen }));
    if (isNowOpen && !detailData[watchId]) {
      try {
        await fetchDetail(watchId);
      } catch {
        throw new Error("Failed to load history");
      }
    }
  };

  /** Opens a row and always refreshes its consumer-safe detail read model. */
  const expandWatch = async (watchId: string) => {
    setExpandedWatches((prev) => ({ ...prev, [watchId]: true }));
    await fetchDetail(watchId);
  };

  const refreshLogs = async (watchId: string) => {
    try {
      setRefreshingLogsWatchId(watchId);
      await fetchDetail(watchId);
    } finally {
      setRefreshingLogsWatchId(null);
    }
  };

  const invalidateWatchDetail = async (watchId: string, refresh = false) => {
    setDetailData((prev) => {
      const remaining = { ...prev };
      delete remaining[watchId];
      return remaining;
    });
    setDetailErrors((prev) => {
      const remaining = { ...prev };
      delete remaining[watchId];
      return remaining;
    });
    if (refresh && expandedWatches[watchId]) {
      await fetchDetail(watchId);
    }
  };

  return {
    expandedWatches,
    setExpandedWatches,
    detailData,
    detailLoading,
    detailErrors,
    refreshingLogsWatchId,
    toggleExpand,
    expandWatch,
    refreshLogs,
    invalidateWatchDetail,
  };
}
